
import { useForm } from "@ispb/react-form"
import { useSyncState } from "@ispb/react-hooks"
import { flow } from "fp-ts/function"
import { GenQLAnonymous } from "genql-anonymous"
import { GenQLPin } from "genql-pin"
import { jwtDecode } from "jwt-decode"
import { compose } from "ramda"
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react"
import { Card, Col, Container, Form, Row } from "react-bootstrap"
import { When } from "react-if"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { req } from "shared/maybe"
import ErrorAlert from "ui/components/ErrorAlert"
import IconText from "ui/components/IconText"
import { STANDARD_SPACING } from "ui/config"
import { bsStringProps, formValidateWithMop } from "ui/form"
import FormOffset from "ui/form/FormOffset"
import FormRow from "ui/form/FormRow"
import FormSubmit from "ui/form/FormSubmit"
import { mapped } from "ui/hoc"
import { ASSET_ICON, SEARCH_ICON } from "ui/icons"
import useTitlePart from "ui/title/useTitlePart"
import WithTitle from "ui/title/WithTitle"
import { ASSET_DELIMITER, GRAPHQL_URL } from "../config.js"
import { Either } from "../namespaces/Either.js"
import { Mop } from "../namespaces/Mop.js"
import { loadData, PublicProcedureContext, useAnonymous } from "./index.js"
import LookupRouter from "./LookupRouter.js"
import { cleanProcedure, PublicProcedure } from "./print.js"

const PIN_SCHEMA = Mop.object({ pin: Mop.maybeOr(Mop.nonBlankString(), () => "") })

interface AnonymousReqs {

    readonly hasura: GenQLPin.Client
    readonly expires: number
    readonly data: PublicProcedure

}

export default compose(
    mapped(() => {
        return {
            passthrough: {},
            map: Mop.object({ barCode: Mop.nonBlankString() })
        }
    })
)(props => {
    const [reqs, setReqs] = useState<AnonymousReqs>()
    if (reqs === undefined) {
        return <LookupForm barCode={props.barCode} setReqs={setReqs} />
    }
    if (reqs.expires <= Date.now()) {
        return <LookupForm barCode={props.barCode} setReqs={setReqs} errors={["Your token is expired"]} />
    }
    return <LookupSuccess data={reqs.data} hasura={reqs.hasura} />
})

interface LookupSuccessProps {

    readonly hasura: GenQLPin.Client
    readonly data: PublicProcedure

}

const LookupSuccess = (props: LookupSuccessProps) => {
    const [data, setData] = useSyncState<PublicProcedure>(props.data)
    const publicProcedure = useMemo(() => {
        return {
            hasura: props.hasura,
            data,
            setData
        }
    }, [
        props.hasura,
        data,
        setData
    ])
    return (
        <PublicProcedureContext.Provider value={publicProcedure}>
            <WithTitle title={data.asset.name + ASSET_DELIMITER + data.asset.location.name}>
                <LookupRouter />
            </WithTitle>
        </PublicProcedureContext.Provider>
    )
}

interface LookupFormProps {

    readonly barCode: string
    readonly errors?: readonly string[] | undefined
    readonly setReqs: Dispatch<SetStateAction<AnonymousReqs | undefined>>

}

const LookupForm = (props: LookupFormProps) => {
    const navigate = useNavigate()
    const params = useParams()
    const [search] = useSearchParams()
    const pin = search.get("pin") ?? undefined
    const submitPin = params["pin"]
    const form = useForm<{ pin?: string }>({
        initial: {
            value: {},
        },
        autoUpdate: true,
        validate: formValidateWithMop(() => PIN_SCHEMA),
        submit: async value => {
            const result = Mop.exec(value, flow(PIN_SCHEMA, Mop.pluck("pin")))
            if (Either.isLeft(result)) {
                return result.left
            }
            const formPin = result.right
            if (formPin !== submitPin) {
                navigate("/" + props.barCode + "/" + formPin)
            }
            else {
                const anonymousHasura = GenQLAnonymous.createClient({ url: GRAPHQL_URL })
                const request = await anonymousHasura.mutation({ pin: { __args: { pin: formPin }, __scalar: true } })
                const payload = jwtDecode(request.pin.token)
                const hasura = GenQLPin.createClient({ url: GRAPHQL_URL, headers: { "authorization": "Bearer " + request.pin.token, "x-hasura-role": "pin" } })
                const data = await loadData(hasura, props.barCode)
                const asset = data.at(0)
                if (asset === undefined) {
                    return [
                        {
                            message: "This asset does not exist or your pin is incorrect",
                            temporary: true,
                        }
                    ]
                }
                const procedure = asset.procedures.at(0)
                if (procedure === undefined) {
                    return [
                        {
                            message: "There is no procedure logged for this asset",
                            temporary: true,
                        }
                    ]
                }
                if (asset.procedures.length > 1) {
                    return [
                        {
                            message: "There are multiple procedures logged for this asset",
                            temporary: true,
                        }
                    ]
                }
                props.setReqs({
                    hasura,
                    expires: req(payload.exp) * 1000,
                    data: cleanProcedure(procedure)
                })
            }
        }
    })
    useEffect(() => {
        if (pin !== undefined) {
            form.setValue({
                pin
            })
        }
    }, [
        pin
    ])
    useEffect(() => {
        if (submitPin !== undefined) {
            form.setValue({ pin: submitPin })
            form.submit()
        }
    }, [
        submitPin
    ])
    const name = search.get("name") ?? undefined
    const companyName = search.get("companyName") ?? undefined
    const [_anonymous, setAnonymous] = useAnonymous()
    useEffect(() => {
        if (name === undefined) {
            return
        }
        if (companyName === undefined) {
            return
        }
        setAnonymous({ name, companyName })
    }, [
        name,
        companyName,
    ])
    const rootErrors = (() => {
        const errors = form.selfErrors?.map(_ => _.message) ?? []
        if (props.errors !== undefined) {
            return [...errors, ...props.errors]//"Your token is expired"]
        }
        return errors
    })()
    useTitlePart("Lookup Asset " + props.barCode)
    return (
        <div className="d-flex flex-column flex-grow-1 justify-content-center align-items-center bg-light">
            <Container>
                <Row>
                    <Col></Col>
                    <Col lg={6} md={8}>
                        <When condition={rootErrors.length > 0}>
                            <ErrorAlert children={rootErrors.join(", ") + "."} className={"mb-" + STANDARD_SPACING} />
                        </When>
                        <Card>
                            <Card.Header className={"p-" + STANDARD_SPACING}>
                                <h5 className="mb-0">
                                    <IconText {...ASSET_ICON} children="View Asset" />
                                </h5>
                            </Card.Header>
                            <Card.Body className="pb-0">
                                <Form onSubmit={form.submit}>
                                    <FormRow label="Asset Pin" field={form.prop("pin")}>
                                        <Form.Control type="text" {...bsStringProps(form.prop("pin"))} />
                                    </FormRow>
                                    <FormOffset>
                                        <div className="d-flex">
                                            <FormSubmit label="Lookup Asset" icon={SEARCH_ICON} form={form} />
                                        </div>
                                    </FormOffset>
                                </Form>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col></Col>
                </Row>
            </Container>
        </div>
    )
}
